import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatError } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs/operators';

import { BasePageComponent } from '@app/pages/base-page/base-page.component';
import { PasswordVisibilityComponent } from '@shared/form-controls';
import { AdcError } from '@shared/models/error.model';
import { AuthService } from '@shared/services';
import { SSO_SUPPORT_URL, SsoLoginService } from '@shared/services/auth/sso-login.service';
import { AdcButtonComponent } from '@shared/ui-kit/adc-button/adc-button.component';
import { AdcCheckboxComponent } from '@shared/ui-kit/adc-checkbox/adc-checkbox.component';
import { AdcInputComponent } from '@shared/ui-kit/adc-input/adc-input.component';
import { ErrorMessageDescriptor } from '@shared/ui-kit/error-message.types';

@Component({
    selector: 'login-page',
    templateUrl: './login-page.component.html',
    styleUrls: ['../../pages.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BasePageComponent,
        ReactiveFormsModule,
        AdcInputComponent,
        PasswordVisibilityComponent,
        MatError,
        MatIcon,
        AdcButtonComponent,
        MatProgressBar,
        RouterLink,
        AdcCheckboxComponent,
    ],
})
export class LoginPageComponent implements OnInit {
    form: UntypedFormGroup;
    loading: boolean;
    loginError: AdcError;

    ssoMode = false;
    ssoLoading = false;

    readonly ssoForm = new FormGroup({
        email: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required, Validators.email],
        }),
    });

    readonly emailErrorMessages: Record<string, string> = {
        required: 'E-mail address is required',
        email: 'Please enter a valid e-mail address',
    };

    readonly ssoEmailErrorMessages: Record<string, ErrorMessageDescriptor> = {
        required: 'E-mail address is required',
        email: 'Please enter a valid e-mail address',
        ssoBlocked: {
            text: 'You cannot log in via SSO with this email address.',
            link: { label: 'Contact support', href: SSO_SUPPORT_URL, target: '_blank' },
        },
    };

    readonly passwordErrorMessages: Record<string, string> = {
        required: 'Password is required',
    };

    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly authService = inject(AuthService);
    private readonly ssoLoginService = inject(SsoLoginService);
    private readonly cdr = inject(ChangeDetectorRef);

    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.initForm();

        this.authService
            .isAuthenticated()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isAuthenticated) => {
                if (isAuthenticated) {
                    this.authService.logout({ clearSession: true });
                }
            });
    }

    login(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.loading = true;
        this.loginError = null;
        this.cdr.markForCheck();

        const { email: username, password, rememberMe } = this.form.value;

        this.authService
            .loginByCredentials({ username, password, rememberMe })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.loading = false;
                    this.cdr.markForCheck();
                }),
            )
            .subscribe({
                error: (error: AdcError) => {
                    this.loginError = error;
                    this.cdr.markForCheck();
                },
            });
    }

    get rememberMeControl(): FormControl<boolean> | null {
        return (this.form.get('rememberMe') as FormControl<boolean>) ?? null;
    }

    enterSsoMode(): void {
        const emailValue = this.form.get('email')?.value as string | null;
        if (emailValue) {
            this.ssoForm.controls.email.setValue(emailValue);
        }

        this.ssoMode = true;
        this.ssoForm.markAsUntouched();
        this.cdr.markForCheck();
    }

    exitSsoMode(): void {
        this.ssoMode = false;
        this.ssoLoading = false;
        this.ssoForm.reset();
        this.cdr.markForCheck();
    }

    ssoLogin(): void {
        if (this.ssoForm.invalid) {
            this.ssoForm.markAllAsTouched();
            return;
        }

        const email = this.ssoForm.controls.email.value;

        this.ssoLoading = true;
        this.cdr.markForCheck();

        this.ssoLoginService
            .probeSSO(email)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.ssoLoading = false;
                    this.cdr.markForCheck();
                }),
            )
            .subscribe((ssoErrorCode: string | null) => {
                if (ssoErrorCode) {
                    this.ssoForm.controls.email.markAsTouched();
                    this.ssoForm.controls.email.setErrors({
                        ssoBlocked: true,
                        ...(this.ssoForm.controls.email.errors ?? {}),
                    });
                }

                this.cdr.markForCheck();
            });
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            email: [null, [Validators.required, Validators.email]],
            password: [null, Validators.required],
            rememberMe: [this.authService.rememberMe()],
        });

        this.form
            .get('rememberMe')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((rememberMe: boolean) => {
                this.authService.rememberMe.set(rememberMe);
            });
    }
}
